import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { API } from "@/App";
import logger from "@/utils/logger";
import { toast } from "sonner";
import adminApi from "./adminApi";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Pencil, Trash2, Plus, Leaf, Star } from "lucide-react";
import ImageUploader from "@/components/ImageUploader";

const CATEGORIES = [
  { value: "starter", label: "Starter" },
  { value: "main", label: "Main Course" },
  { value: "dessert", label: "Dessert" },
  { value: "drink", label: "Drink" },
  { value: "side", label: "Side Dish" },
];

const TYPES = [
  { value: "wedding", label: "Wedding" },
  { value: "corporate", label: "Corporate" },
  { value: "traditional", label: "Traditional" },
  { value: "private", label: "Private" },
];

const emptyForm = {
  name: "", description: "", price: 0, category: "main",
  package_type: "wedding", image_url: "", is_vegetarian: false, is_featured: false,
};

const MenuItemForm = ({ initial, onSave, onCancel, saving }) => {
  const [form, setForm] = useState(initial || emptyForm);
  const update = (key, val) => setForm((p) => ({ ...p, [key]: val }));
  const isValid = form.name && form.description && form.category;

  return (
    <div className="space-y-4">
      <div>
        <Label className="mb-1 block">Dish Name</Label>
        <Input value={form.name} onChange={(e) => update("name", e.target.value)} data-testid="menu-item-name-input" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="mb-1 block">Category</Label>
          <Select value={form.category} onValueChange={(v) => update("category", v)}>
            <SelectTrigger data-testid="menu-item-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="mb-1 block">Package Type</Label>
          <Select value={form.package_type} onValueChange={(v) => update("package_type", v)}>
            <SelectTrigger data-testid="menu-item-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              {TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label className="mb-1 block">Description</Label>
        <Textarea value={form.description} onChange={(e) => update("description", e.target.value)} rows={2} data-testid="menu-item-desc-input" />
      </div>
      <div>
        <Label className="mb-1 block">Price ($)</Label>
        <Input type="number" step="0.01" value={form.price} onChange={(e) => update("price", e.target.value)} data-testid="menu-item-price-input" />
      </div>
      <div>
        <Label className="mb-1 block">Image</Label>
        <ImageUploader currentUrl={form.image_url} onUploaded={(url) => update("image_url", url)} />
      </div>
      <div className="flex items-center gap-6">
        <div className="flex items-center gap-2">
          <Switch checked={form.is_vegetarian} onCheckedChange={(v) => update("is_vegetarian", v)} />
          <Label>Vegetarian</Label>
        </div>
        <div className="flex items-center gap-2">
          <Switch checked={form.is_featured} onCheckedChange={(v) => update("is_featured", v)} />
          <Label>Featured</Label>
        </div>
      </div>
      <div className="flex gap-3 pt-2">
        <Button onClick={() => onSave({ ...form, price: parseFloat(form.price) || 0 })} disabled={saving || !isValid} data-testid="menu-item-save-btn" className="bg-[#2D6A4F] hover:bg-[#245a42] text-white">
          {saving ? "Saving..." : "Save"}
        </Button>
        {onCancel && <Button variant="outline" onClick={onCancel}>Cancel</Button>}
      </div>
    </div>
  );
};

const MenuItemsManagement = () => {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [filterCategory, setFilterCategory] = useState("all");

  const fetchItems = useCallback(async () => {
    try {
      const response = await adminApi.get(`${API}/menu-items`);
      setItems(response.data);
    } catch (error) {
      logger.error("Error fetching menu items:", error);
    } finally {
      setLoading(false);
    }
  }, [API, adminApi]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleCreate = async (form) => {
    setSaving(true);
    try {
      await adminApi.post(`${API}/menu-items`, form);
      toast.success("Menu item added!");
      setShowAdd(false);
      fetchItems();
    } catch { toast.error("Failed to add menu item"); }
    finally { setSaving(false); }
  };

  const handleUpdate = async (form) => {
    setSaving(true);
    try {
      await adminApi.put(`${API}/menu-items/${editingId}`, form);
      toast.success("Menu item updated!");
      setEditingId(null);
      fetchItems();
    } catch { toast.error("Failed to update menu item"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this menu item?")) return;
    try {
      await adminApi.delete(`${API}/menu-items/${id}`);
      toast.success("Menu item deleted!");
      fetchItems();
    } catch { toast.error("Failed to delete menu item"); }
  };

  const filtered = filterCategory === "all" ? items : items.filter((i) => i.category === filterCategory);

  return (
    <div data-testid="menu-items-management" className="space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#3D405B] font-['Playfair_Display']">Menu Items</h1>
          <p className="text-[#6B705C]">Manage dishes shown on the menu page</p>
        </div>
        <Dialog open={showAdd} onOpenChange={setShowAdd}>
          <DialogTrigger asChild>
            <Button className="bg-[#2D6A4F] hover:bg-[#245a42] text-white" data-testid="add-menu-item-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Dish
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Add Menu Item</DialogTitle></DialogHeader>
            <MenuItemForm onSave={handleCreate} saving={saving} />
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex gap-2 flex-wrap">
        <Button variant={filterCategory === "all" ? "default" : "outline"} size="sm" onClick={() => setFilterCategory("all")} className={filterCategory === "all" ? "bg-[#3D405B]" : ""}>
          All ({items.length})
        </Button>
        {CATEGORIES.map((c) => {
          const count = items.filter((i) => i.category === c.value).length;
          return (
            <Button key={c.value} variant={filterCategory === c.value ? "default" : "outline"} size="sm" onClick={() => setFilterCategory(c.value)} className={filterCategory === c.value ? "bg-[#3D405B]" : ""}>
              {c.label} ({count})
            </Button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="spinner"></div></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((item) => (
            <Card key={item.id} className="bg-white border-0 shadow-sm overflow-hidden" data-testid={`admin-menu-item-${item.id}`}>
              {editingId === item.id ? (
                <CardContent className="p-5">
                  <MenuItemForm
                    initial={item}
                    onSave={handleUpdate}
                    onCancel={() => setEditingId(null)}
                    saving={saving}
                  />
                </CardContent>
              ) : (
                <>
                  {item.image_url && (
                    <img src={item.image_url} alt={item.name} className="w-full h-40 object-cover" />
                  )}
                  <CardContent className="p-5">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex gap-1.5 flex-wrap">
                        <Badge className="bg-[#2D6A4F]/10 text-[#2D6A4F] text-xs">{item.category}</Badge>
                        <Badge variant="outline" className="text-xs">{item.package_type}</Badge>
                        {item.is_vegetarian && <Badge className="bg-green-100 text-green-700 text-xs"><Leaf className="w-3 h-3 mr-0.5" />Veg</Badge>}
                        {item.is_featured && <Badge className="bg-amber-100 text-amber-700 text-xs"><Star className="w-3 h-3 mr-0.5" />Featured</Badge>}
                      </div>
                      <div className="flex gap-0.5 shrink-0">
                        <Button variant="ghost" size="sm" onClick={() => setEditingId(item.id)} data-testid={`edit-menu-item-${item.id}`}>
                          <Pencil className="w-4 h-4 text-[#3D405B]" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(item.id)} className="text-red-600">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <h3 className="font-semibold text-[#3D405B] text-lg">{item.name}</h3>
                    <p className="text-sm text-[#6B705C] mt-1 line-clamp-2">{item.description}</p>
                    <p className="font-medium text-[#E07A5F] mt-3">${item.price?.toFixed(2)}</p>
                  </CardContent>
                </>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default MenuItemsManagement;
